import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "@remix-run/react";
import { Check, ChevronDown, ChevronRight, Loader2, Wand2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "~/components/ui/button";
import { InlineEditableText } from "~/components/ui/inline-editable-text";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { useUnsavedCaptureWarning } from "~/features/meditations/hooks/use-unsaved-capture-warning";
import type { UgcProvenance } from "~/lib/ugc-provenance";
import { cn } from "~/lib/utils";

// AIDEV-NOTE: Config-driven in-meditation capture rail, mounted at the right of the fullscreen
// player. `captureKind` selects the save target so ONE panel serves every meditation surface:
//   - "pcc_card"          → /api/parts/:id/user-fields (structured DPMP card → the part's durable
//                            part_profile; user-authored provenance tier that wins over inferred
//                            values). The kind literal stays "pcc_card" because it's referenced by
//                            resource experience configs stored in the DB.
//   - "note"              → /api/parts/:id/notes (biography — narrative prose composed into one
//                            note body; POST creates then PATCH updates).
//   - "part_note"         → /api/parts/:id/notes (standard journal note — single free-text box,
//                            NOT DPMP-structured; POST creates then PATCH updates the same note
//                            within the session; append-style, a fresh mount starts a new entry).
//   - "name"              → /api/parts/:id/rename only (single inline field, no card/note).
//   - "meditation_journal"→ /api/meditation/journal (expanded reflection field,
//                            saved as ONE note linked from meditation.journalNoteId; the default for
//                            general/non-DPMP meditations).
// Focusing a field pauses the audio (onFieldFocus) so the user can step out to write.
//
// AIDEV-NOTE: NO autosave (Jul 2026, Michelle Glass). Nothing persists until the user clicks Save —
// including the discrete name/age/gender/classification editors, which only mutate local state.
// The single Save handler folds the rename in (name uses a separate /rename endpoint) before
// writing the card. In the fullscreen player the host passes `onClose`, turning Save into
// "Save & Close" (persists then closes the overlay); on the inline resource page there's no onClose,
// so Save persists and pops a confirmation toast in place.

export type CaptureKind =
    | "pcc_card"
    | "note"
    | "part_note"
    | "name"
    | "meditation_journal";

export interface CaptureField {
    key: string;
    label: string;
    prompt?: string;
    placeholder?: string;
    // AIDEV-NOTE: When present, the field renders as a dropdown (e.g. gender) instead of a textarea.
    options?: string[];
}

// AIDEV-NOTE: Gender = identity (per Michelle Glass: "different parts have different gender
// identities"), NOT sex — so Feminine/Masculine, not Female/Male. Classification = the
// manager/firefighter/exile/guide/unattached-burden taxonomy. Both are config-driven dropdowns;
// these are only fallbacks if the experience config omits `options`.
const GENDER_FALLBACK_OPTIONS = [
    "Feminine",
    "Masculine",
    "Non-binary",
    "Neutral",
    "Unsure",
];
/**
 * Map a part's stored `type` onto one of the Type dropdown's options.
 *
 * AIDEV-NOTE: An exact case-insensitive match is not enough and silently failed for the
 * MOST COMMON type we hold. Stored values look like "Protector (Manager)",
 * "Protector (Firefighter)", "Protector (Self-like)", "Self-like part" — none of which
 * equals an option, so the dropdown opened on "Select" for roughly three quarters of parts
 * and the pre-population looked broken rather than absent.
 *
 * The parenthetical is the SPECIFIC subtype and deliberately wins over the generic head
 * noun: "Protector (Manager)" is Manager, not Protector. Matching is done against the
 * options actually passed in, so editing the list in the CMS does not strand this.
 */
// AIDEV-NOTE: The ONE parenthetical in the data that is a certainty STATUS rather than a
// subtype. "Unattached Burden (Unconfirmed)" and "(Confirmed)" are real stored values (28
// rows in staging), and the subtype rule below read them as the subtype "Unconfirmed",
// which matches nothing — and neither did the whole string, so the dropdown opened on
// "Select" for every unattached burden. Stripped FIRST so what is left ("Unattached
// Burden") resolves normally. Keep this list to statuses; a new subtype belongs in
// parentheses and must keep flowing through the candidate list.
const STATUS_PARENTHETICAL = /\s*\((?:un)?confirmed\)\s*$/i;

function resolveClassificationOption(
    partType: string | null | undefined,
    options: string[],
): string | undefined {
    if (!partType?.trim()) return undefined;
    const norm = (value: string) => value.toLowerCase().replace(/[^a-z]/g, "");
    const base = partType.replace(STATUS_PARENTHETICAL, "").trim();

    const candidates = [
        base.match(/\(([^)]+)\)/)?.[1], // "Protector (Manager)" -> "Manager"
        base, // the whole string, status parenthetical removed
        base.replace(/\s*part$/i, ""), // "Self-like part" -> "Self-like"
    ].filter((value): value is string => Boolean(value?.trim()));

    for (const candidate of candidates) {
        const target = norm(candidate);
        if (!target) continue;
        const exact = options.find((option) => norm(option) === target);
        if (exact) return exact;
        // "Self-like" -> "Self-Like Manager". Prefix only, so "Exile" can never reach a
        // longer unrelated option.
        const prefixed = options.find((option) =>
            norm(option).startsWith(target),
        );
        if (prefixed) return prefixed;
    }
    return undefined;
}

const CLASSIFICATION_FALLBACK_OPTIONS = [
    "Manager",
    "Self-Like Manager",
    "Firefighter",
    "Protector",
    "Exile",
    "Guide",
    "Unattached burden",
];

type SaveState = "idle" | "saving" | "saved" | "error";

// AIDEV-NOTE: Outcome of a save() call so the explicit Save button can pop the right toast:
// "empty" = nothing to persist yet, "saved" = success, "error" = soft-failed (kept on screen).
type SaveResult = "saved" | "error" | "empty";

// AIDEV-NOTE: Serialize saves so a create (POST) fully returns and records its id before the next
// save runs. Without this, two concurrent saves (e.g. immediate age/gender dropdown commits, or a
// blur-save overlapping the unmount flush) both POST with a still-null noteIdRef and insert
// duplicate rows — or the trailing save creates a second note that loses the latest text (QA: A1
// duplicate cards; G6/G8 duplicate/lost notes, Jul 2026). Each call chains onto the previous one
// regardless of its outcome; the wrapped fn reads the latest refs at execution time so no edits are
// dropped. The returned callback is stable (empty deps) yet always invokes the freshest fn via ref.
function useSerializedSave<T>(fn: () => Promise<T>) {
    const fnRef = useRef(fn);
    fnRef.current = fn;
    const chainRef = useRef<Promise<unknown>>(Promise.resolve());
    return useCallback(() => {
        const next = chainRef.current
            .catch(() => {})
            .then(() => fnRef.current());
        chainRef.current = next;
        return next;
    }, []);
}

// AIDEV-NOTE: Inverse of composeBody() — parse a saved biography note body back into per-field
// values. Sections are delimited by a line equal to a known field label; everything until the next
// label line is that field's content (tolerates blank lines inside a section).
function parseComposedBody(
    body: string,
    fields: CaptureField[],
): Record<string, string> {
    const labelToKey = new Map(fields.map((f) => [f.label, f.key]));
    const out: Record<string, string> = {};
    let currentKey: string | null = null;
    let buffer: string[] = [];
    const flush = () => {
        if (currentKey) out[currentKey] = buffer.join("\n").trim();
        buffer = [];
    };
    for (const line of body.split("\n")) {
        const key = labelToKey.get(line.trim());
        if (key) {
            flush();
            currentKey = key;
        } else if (currentKey) {
            buffer.push(line);
        }
    }
    flush();
    return out;
}

interface Props {
    captureKind: CaptureKind;
    title: string;
    // AIDEV-NOTE: Structured fields for pcc_card / note kinds. Unused by name / meditation_journal.
    fields?: CaptureField[];
    // Part context (required for pcc_card / note / name).
    partId?: string;
    partName?: string;
    // AIDEV-NOTE: The part's INFERRED type (part.type from the pipeline). Pre-populates the
    // pcc_card classification dropdown and drives which type-conditional fields render; saves
    // with the card unless the user changes it.
    partType?: string | null;
    // Meditation context (required for meditation_journal).
    sessionId?: string;
    partInteractionId?: string;
    meditationType?: string;
    /** Concrete meditation row to save against; falls back to the context lookup. */
    meditationId?: string | null;
    onFieldFocus?: () => void;
    // AIDEV-NOTE: meditation_journal only. Renders a "Skip" beside Save & Close so the journal
    // that auto-opens when a generated meditation completes is visibly optional (Brent,
    // 2026-09-10). Closes without saving; the top-right X does the same.
    onSkip?: () => void;
    // AIDEV-NOTE: Fired when a save persisted a DPMP-only field, so the host can offer the
    // add-on to someone capturing a healing record the product will not yet show them.
    onDpmpFieldsSaved?: () => void;
    // AIDEV-NOTE: Fired after a successful rename (name / pcc_card kinds) so the host (e.g. the
    // resource-page part carousel) can reflect the new name without a full reload.
    onRename?: (partId: string, newName: string) => void;
    // AIDEV-NOTE: Present only in the fullscreen player (not the inline resource panel). When set,
    // Save becomes "Save & Close" — it persists then invokes onClose to dismiss the overlay.
    onClose?: () => void;
    // AIDEV-NOTE: Measurement only — WHERE this rail is mounted (in-player vs inline resource
    // panel) and which experience it belongs to, stamped onto every row it writes so resource
    // captures are separable from part-page ones in SQL. Never affects what is saved.
    provenance?: UgcProvenance;
}

// AIDEV-NOTE: The capture fields that only mean something with the DPMP® add-on on —
// keep in lockstep with features/resources/server/pcc-card-attributes.ts.
// AIDEV-NOTE: The "About this part" step is a GROUP of attributes, not one note field, so
// it has no CaptureField key of its own — this stands in for one in the single-open
// accordion's state. Prefixed so it can never collide with a real field key.
const ABOUT_STEP_KEY = "__about";

// AIDEV-NOTE: EVO-2477 — "is there typed text that Save has not persisted yet". Key-sorted and
// trimmed so field re-ordering and trailing whitespace don't read as an edit, and blank fields are
// dropped so clearing a box back to empty matches the empty snapshot.
function captureSnapshot(values: Record<string, string>): string {
    return JSON.stringify(
        Object.keys(values)
            .sort()
            .map((key) => [key, (values[key] ?? "").trim()])
            .filter(([, value]) => value !== ""),
    );
}

const EMPTY_CAPTURE_SNAPSHOT = captureSnapshot({});

const DPMP_CAPTURE_FIELD_KEYS = new Set([
    "new_role",
    "where_retrieved",
    "what_unburdened",
    "qualities_reclaimed",
]);

export function MeditationCapturePanel(props: Props) {
    switch (props.captureKind) {
        case "name":
            return (
                <NameCapture
                    title={props.title}
                    partId={props.partId ?? ""}
                    partName={props.partName}
                    onRename={props.onRename}
                    onClose={props.onClose}
                    provenance={props.provenance}
                />
            );
        case "meditation_journal":
            return (
                <ReflectCapture
                    sessionId={props.sessionId}
                    partInteractionId={props.partInteractionId}
                    meditationType={props.meditationType}
                    meditationId={props.meditationId}
                    onFieldFocus={props.onFieldFocus}
                    onClose={props.onClose}
                    onSkip={props.onSkip}
                    provenance={props.provenance}
                />
            );
        case "note":
            return (
                <NoteCapture
                    title={props.title}
                    partId={props.partId ?? ""}
                    partName={props.partName}
                    fields={props.fields ?? []}
                    onFieldFocus={props.onFieldFocus}
                    onClose={props.onClose}
                    provenance={props.provenance}
                />
            );
        case "part_note":
            return (
                <PartNoteCapture
                    title={props.title}
                    partId={props.partId ?? ""}
                    partName={props.partName}
                    field={props.fields?.[0]}
                    onFieldFocus={props.onFieldFocus}
                    onClose={props.onClose}
                    provenance={props.provenance}
                />
            );
        case "pcc_card":
        default:
            return (
                <PccCardCapture
                    title={props.title}
                    partId={props.partId ?? ""}
                    fields={props.fields ?? []}
                    partName={props.partName}
                    partType={props.partType}
                    onFieldFocus={props.onFieldFocus}
                    onRename={props.onRename}
                    onClose={props.onClose}
                    provenance={props.provenance}
                    onDpmpFieldsSaved={props.onDpmpFieldsSaved}
                />
            );
    }
}

// AIDEV-NOTE: Magic-wand suggest button (Michelle Glass, Jul 2026 — hint-only per Jul 7 decision).
// Floats right on the field's prompt row inside an open accordion field; on click it asks the
// server to draft this field from the part's attributes-to-date and shows it in a POPOVER as a
// hint. It deliberately does NOT insert into the textarea and offers no adopt button — we value
// user-generated content, so we only hint at what Parts Companion thinks it knows and encourage
// the user to write in their own words. Only wired for pcc_card prose fields (onSuggest).
/**
 * Suggest — the magic-wand hint, split into a trigger and a hint line.
 *
 * AIDEV-NOTE: Two nodes, one state, because the handoff puts them in different places:
 * the pill sits in the step's button row and the hint belongs UNDER the prompt, above
 * the textarea. Returning both from one component meant the hint inherited whatever
 * layout the trigger was in — first a popover that covered the field you were about to
 * type into, then a flex cell that wrapped it to a few characters per line. A hook keeps
 * them one piece of state while letting the step place each of them.
 *
 * Hint-only, always: it shows the draft and NEVER writes into the field.
 */
function useFieldSuggestion(
    fieldLabel: string,
    // AIDEV-NOTE: Optional, and the hook still runs when it is absent — only prose fields
    // get a Suggest, so calling the hook conditionally would break the rules of hooks the
    // moment a field's kind changed under the same mount.
    onSuggest?: () => Promise<string>,
) {
    const [suggesting, setSuggesting] = useState(false);
    const [suggestion, setSuggestion] = useState("");

    const handleClick = async () => {
        if (!onSuggest) return;
        if (suggestion) {
            setSuggestion("");
            return;
        }
        setSuggesting(true);
        try {
            const drafted = await onSuggest();
            if (drafted) {
                setSuggestion(drafted);
            } else {
                toast("No suggestion yet — tell your part's story first.");
            }
        } catch {
            toast.error("Couldn't draft a suggestion. Please try again.");
        } finally {
            setSuggesting(false);
        }
    };

    // AIDEV-NOTE: DS secondary pill, 28px, white on a forest hairline per 2e. It used to be
    // terracotta, which read as the step's primary action rather than an aside to it.
    const trigger = !onSuggest ? null : (
        <button
            type="button"
            onClick={handleClick}
            disabled={suggesting}
            aria-expanded={Boolean(suggestion)}
            aria-label={`Hint for ${fieldLabel} from what Parts Companion has noticed`}
            className="inline-flex h-7 shrink-0 items-center gap-1 rounded-[20px] border border-brand-forest-200 bg-white px-3 text-xs font-medium text-brand-forest-800 transition-colors hover:bg-brand-forest-50 disabled:opacity-60"
        >
            {suggesting ? (
                <Loader2 className="size-3.5 animate-spin" />
            ) : (
                <Wand2 className="size-3.5" />
            )}
            Suggest
        </button>
    );

    const hint =
        suggestion && onSuggest ? (
            <p
                data-testid="suggest-hint"
                className="mt-2 flex items-start gap-2 text-sm italic leading-[1.5] text-brand-forest-600"
            >
                <Wand2 className="mt-[3px] size-3.5 shrink-0 not-italic text-brand-forest-600" />
                <span>
                    {suggestion}
                    <span className="not-italic text-brand-forest-600"> </span>
                </span>
            </p>
        ) : null;

    return { trigger, hint };
}

// AIDEV-NOTE: Shared single-open accordion textarea used by both pcc_card and note kinds.
/**
 * Chrome shared by every step of the capture accordion (handoff 2d/2e).
 *
 * AIDEV-NOTE: One step has three states and the design gives each its own card, so the
 * chrome lives here rather than being restated per step:
 *   open      forest-300 border, soft shadow, dark numbered badge, body visible
 *   done      hairline border, forest-700 badge with a check, one-line preview, "Edit"
 *   pending   hairline border, outlined badge, muted label, chevron
 * `index` is the user-facing step number, so it is 1-based at the call site.
 */
function CaptureStep({
    index,
    label,
    isOpen,
    isDone,
    preview,
    onToggle,
    children,
}: {
    /** Omitted for the non-stepped captures, which show a plain header. */
    index?: number;
    label: string;
    isOpen: boolean;
    isDone: boolean;
    /** One-line summary shown only when the step is done and collapsed. */
    preview?: string;
    onToggle: () => void;
    children?: React.ReactNode;
}) {
    if (!isOpen) {
        return (
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={false}
                data-testid="capture-step"
                data-state={isDone ? "done" : "pending"}
                className="flex w-full items-center gap-3 rounded-[14px] border border-brand-forest-100 bg-white px-3.5 py-[5px] text-left"
            >
                {(index !== undefined || isDone) && (
                    <span
                        className={cn(
                            "grid size-6 shrink-0 place-items-center rounded-full text-xs font-semibold",
                            isDone
                                ? "bg-brand-forest-700 text-white"
                                : "border border-brand-forest-200 bg-white text-brand-forest-600",
                        )}
                    >
                        {isDone ? <Check className="size-3.5" /> : index}
                    </span>
                )}
                <span className="min-w-0 flex-1">
                    <span
                        className={cn(
                            "block text-[15px] font-semibold",
                            isDone
                                ? "text-brand-forest-800"
                                : "text-brand-forest-600",
                        )}
                    >
                        {label}
                    </span>
                    {isDone && preview && (
                        <span className="mt-px block truncate text-[13px] text-brand-forest-600">
                            {preview}
                        </span>
                    )}
                </span>
                {isDone ? (
                    <span className="shrink-0 text-[13px] font-medium text-brand-forest-700">
                        Edit
                    </span>
                ) : (
                    <ChevronDown className="size-4 shrink-0 text-brand-forest-400" />
                )}
            </button>
        );
    }

    return (
        <div
            data-testid="capture-step"
            data-state="open"
            className="rounded-[14px] border border-brand-forest-300 bg-white px-3.5 py-3 shadow-[0_2px_10px_rgba(27,67,50,0.06)]"
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded
                className="flex w-full items-center gap-3 text-left"
            >
                {index !== undefined && (
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-brand-forest-800 text-xs font-semibold text-white">
                        {index}
                    </span>
                )}
                <span className="flex-1 text-base font-semibold text-brand-forest-800">
                    {label}
                </span>
                <ChevronDown className="size-4 shrink-0 rotate-180 text-brand-forest-400" />
            </button>
            {children}
        </div>
    );
}

/**
 * The Skip / Next pair that advances the accordion (handoff 2d/2e).
 *
 * AIDEV-NOTE: Next is disabled until the step has content; Skip collapses it without any.
 * Both are 36px, which is under the 44px mobile target, so the row pads itself to 44px —
 * the design calls for the smaller button with the larger tap area, not a smaller target.
 */
function StepAdvanceRow({
    canAdvance,
    onSkip,
    onNext,
    isLast,
    leading,
}: {
    canAdvance: boolean;
    onSkip: () => void;
    onNext: () => void;
    /** The last step has nowhere to advance to, so it only offers to collapse. */
    isLast: boolean;
    /** Slot for the Suggest pill, which sits opposite the pair. */
    leading?: React.ReactNode;
}) {
    return (
        <div className="mt-1.5 flex min-h-11 items-center gap-2">
            {leading}
            <span className="flex-1" />
            {!isLast && (
                <button
                    type="button"
                    onClick={onSkip}
                    className="inline-flex h-9 items-center rounded-[18px] px-4 text-sm font-medium text-brand-forest-700"
                >
                    Skip
                </button>
            )}
            <button
                type="button"
                onClick={onNext}
                disabled={!canAdvance}
                data-testid="capture-step-next"
                className={cn(
                    "inline-flex h-9 items-center gap-1.5 rounded-[18px] bg-brand-forest-800 px-4 text-sm font-medium text-white",
                    !canAdvance && "opacity-50",
                )}
            >
                {isLast ? "Done" : "Next"}
                {!isLast && <ChevronRight className="size-4" />}
            </button>
        </div>
    );
}

function AccordionField({
    field,
    index,
    isOpen,
    isDone,
    isLast = false,
    onToggle,
    onNext,
    onSkip,
    value,
    onChange,
    onFocus,
    onSuggest,
}: {
    field: CaptureField;
    /** AIDEV-NOTE: Step number, and the signal that this field IS a step. The biography,
     * part-note and reflect captures compose several prompts into one note and have their
     * own Save, so they pass neither this nor onNext and get no number and no Skip/Next. */
    index?: number;
    isOpen: boolean;
    isDone?: boolean;
    isLast?: boolean;
    onToggle: () => void;
    onNext?: () => void;
    onSkip?: () => void;
    value: string;
    onChange: (value: string) => void;
    onFocus?: () => void;
    // AIDEV-NOTE: Present only for pcc_card prose fields → renders the magic-wand suggest button.
    onSuggest?: () => Promise<string>;
}) {
    const suggest = useFieldSuggestion(field.label, onSuggest);

    // AIDEV-NOTE: Some capture prompts are a paragraph plus a five-bullet list (the DPMP
    // journal fields in resource_experience.config). At full length they push the writing
    // box off the bottom of a phone, so the step is a wall of instructions with nowhere to
    // type. Long prompts open clamped with a toggle; short ones are untouched, and the
    // copy itself is never altered.
    const [promptExpanded, setPromptExpanded] = useState(false);
    const isLongPrompt = (field.prompt?.length ?? 0) > 180;

    return (
        <CaptureStep
            index={index}
            label={field.label}
            isOpen={isOpen}
            isDone={isDone ?? Boolean(value.trim())}
            preview={value.trim()}
            onToggle={onToggle}
        >
            {field.prompt && (
                <>
                    <p
                        className={cn(
                            "mt-1.5 whitespace-pre-line text-[15px] leading-[1.5] text-brand-forest-700",
                            isLongPrompt &&
                                !promptExpanded &&
                                "line-clamp-3 sm:line-clamp-none",
                        )}
                    >
                        {field.prompt}
                    </p>
                    {isLongPrompt && (
                        <button
                            type="button"
                            onClick={() => setPromptExpanded((v) => !v)}
                            className="mt-1 text-sm font-medium text-brand-terra-600 sm:hidden"
                        >
                            {promptExpanded ? "Show less" : "Show more"}
                        </button>
                    )}
                </>
            )}
            {/* The hint sits under the prompt, above the field it is about (2e). */}
            {suggest.hint}
            <Textarea
                id={`capture-${field.key}`}
                value={value}
                placeholder={field.placeholder}
                onFocus={onFocus}
                onChange={(e) => onChange(e.target.value)}
                className="mt-2 min-h-[140px] resize-y rounded-xl border-brand-forest-200 bg-brand-cream-50 px-3.5 py-3 text-[17px] leading-[1.6] placeholder:text-brand-forest-400 sm:min-h-[88px]"
            />
            {onNext && onSkip ? (
                <StepAdvanceRow
                    canAdvance={value.trim().length > 0}
                    onSkip={onSkip}
                    onNext={onNext}
                    isLast={isLast}
                    leading={suggest.trigger ?? undefined}
                />
            ) : (
                suggest.trigger && (
                    <div className="mt-1.5 flex min-h-11 items-center">
                        {suggest.trigger}
                    </div>
                )
            )}
        </CaptureStep>
    );
}

/**
 * Step 1 — the part's own attributes (handoff 2d).
 *
 * AIDEV-NOTE: Name, age, gender and type were two standalone cards above the accordion.
 * They are one step now, as label/value rows on a 64px label column so the values have
 * room (long part names are the norm, not the exception). They stay SEPARATE rows rather
 * than one joined line because each is its own stored attribute — name goes through
 * /rename, the other three through user-fields. Nothing here persists until Save.
 */
function AboutPartStep({
    index,
    isOpen,
    isDone,
    isLast,
    onToggle,
    onNext,
    onSkip,
    hasNameField,
    localName,
    setLocalName,
    ageField,
    genderField,
    classificationField,
    classificationOptions,
    typeIsInferred,
    values,
    commitValue,
}: {
    index: number;
    isOpen: boolean;
    isDone: boolean;
    isLast: boolean;
    onToggle: () => void;
    onNext: () => void;
    onSkip: () => void;
    hasNameField: boolean;
    localName: string;
    setLocalName: (v: string) => void;
    ageField?: CaptureField;
    genderField?: CaptureField;
    classificationField?: CaptureField;
    classificationOptions: string[];
    /** The type was pre-filled from the pipeline's inference and not changed since. */
    typeIsInferred: boolean;
    values: Record<string, string>;
    commitValue: (key: string, value: string) => void;
}) {
    const preview = [
        hasNameField ? localName : null,
        values.age,
        values.gender,
        values.type_of_part,
    ]
        .filter((v) => v && v.trim())
        .join(" · ");

    const rows: { label: string; node: React.ReactNode }[] = [];
    if (hasNameField) {
        rows.push({
            label: "Name",
            node: (
                <InlineEditableText
                    value={localName}
                    onSave={async (v) => setLocalName(v)}
                    commitOnBlur
                    alwaysShowPencil
                    className="text-base text-brand-forest-800"
                    inputClassName="h-9 text-base"
                    pencilClassName="text-brand-forest-500"
                    placeholder="Name this part"
                />
            ),
        });
    }
    if (ageField) {
        rows.push({
            label: ageField.label,
            node: (
                // AIDEV-NOTE: commitOnBlur (EVO-2215) — Age is the only free-text field in
                // this step. Its check button always worked, but production showed nobody
                // reached it: 0 typed ages against 16/17 genders on the same saves.
                <InlineEditableText
                    value={values.age ?? ""}
                    onSave={async (v) => commitValue("age", v)}
                    commitOnBlur
                    alwaysShowPencil
                    className="text-base text-brand-forest-800"
                    inputClassName="h-9 text-base"
                    pencilClassName="text-brand-forest-500"
                    placeholder={ageField.placeholder ?? "Add age"}
                />
            ),
        });
    }
    if (genderField) {
        rows.push({
            label: genderField.label,
            node: (
                <Select
                    value={values.gender ?? ""}
                    onValueChange={(v) => commitValue("gender", v)}
                >
                    <SelectTrigger className="h-9 border-0 bg-transparent px-0 text-base shadow-none focus:ring-0">
                        <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                        {(genderField.options ?? GENDER_FALLBACK_OPTIONS).map(
                            (opt) => (
                                <SelectItem key={opt} value={opt}>
                                    {opt}
                                </SelectItem>
                            ),
                        )}
                    </SelectContent>
                </Select>
            ),
        });
    }
    if (classificationField) {
        rows.push({
            label: classificationField.label,
            node: (
                <>
                    <Select
                        value={values.type_of_part ?? ""}
                        onValueChange={(v) => commitValue("type_of_part", v)}
                    >
                        <SelectTrigger className="h-9 w-auto border-0 bg-transparent px-0 text-base shadow-none focus:ring-0">
                            <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                            {classificationOptions.map((opt) => (
                                <SelectItem key={opt} value={opt}>
                                    {opt}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {/* AIDEV-NOTE: "Inferred" is a provenance label, not a control — it says
                        the pipeline filled this in so the user knows to correct it rather
                        than wondering where it came from. It disappears once they do. */}
                    {typeIsInferred && (
                        <span className="shrink-0 rounded-full bg-brand-forest-100 px-2 py-0.5 text-[11px] font-semibold text-brand-forest-700">
                            Inferred
                        </span>
                    )}
                </>
            ),
        });
    }

    return (
        <CaptureStep
            index={index}
            label="About this part"
            isOpen={isOpen}
            isDone={isDone}
            preview={preview}
            onToggle={onToggle}
        >
            <div className="mt-1.5 flex flex-col">
                {rows.map((row, i) => (
                    <div
                        key={row.label}
                        className={cn(
                            // AIDEV-NOTE: py-[7px] + min-h-[34px], so a wrapping part name
                            // keeps its vertical padding instead of being squeezed by the
                            // row's minimum.
                            "flex min-h-[34px] items-center gap-3 py-[7px] leading-[1.3]",
                            i < rows.length - 1 &&
                                "border-b border-brand-forest-100",
                        )}
                    >
                        <span className="w-16 shrink-0 text-[11px] font-semibold uppercase tracking-[0.35px] text-brand-terra-600">
                            {row.label}
                        </span>
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                            {row.node}
                        </span>
                    </div>
                ))}
            </div>
            {/* AIDEV-NOTE: Always advanceable — the name arrives pre-filled, and age, gender
                and type are optional by design. */}
            <StepAdvanceRow
                canAdvance
                onSkip={onSkip}
                onNext={onNext}
                isLast={isLast}
            />
        </CaptureStep>
    );
}

/**
 * Where what you write ends up.
 *
 * AIDEV-NOTE: This used to be a sentence in the section BODY, which put it above the part
 * picker — promising that writing would be saved on the step before there was anything to
 * write. It belongs on the step that actually has the fields, so it lives here, next to
 * them.
 *
 * AIDEV-NOTE: Renders on the three kinds that WRITE prose to a part — pcc_card, note and
 * part_note. Not on meditation_journal, which has no part to save against, and not on
 * `name`, whose single inline field IS the part's name, so "what you write is saved to this
 * part's record" would be telling the user something the field already says.
 */
function CaptureDestinationNote() {
    return (
        <p className="-mt-1 text-sm text-brand-forest-600">
            What you write is saved to this part&apos;s record.
        </p>
    );
}

// AIDEV-NOTE: pcc_card kind — the DPMP Parts Catalogue Card. Saves ONLY on the explicit Save
// button to /api/parts/:id/user-fields, which upserts the part's durable part_profile + a
// day-coalesced revision + timeline interaction (user-authored provenance tier). Everything is
// resolved server-side from partId — no record ids are round-tripped. `name` edits the real part
// name (folded into Save via a separate /rename call); age/gender/classification share a compact
// row; roles is hoisted first; everything else is a single-open accordion textarea. NOTHING
// persists until Save — every editor here mutates local state only.
function PccCardCapture({
    partId,
    title,
    fields,
    partName,
    partType,
    onFieldFocus,
    onRename,
    onClose,
    provenance,
    onDpmpFieldsSaved,
}: {
    partId: string;
    title: string;
    fields: CaptureField[];
    partName?: string;
    partType?: string | null;
    onFieldFocus?: () => void;
    onRename?: (partId: string, newName: string) => void;
    onClose?: () => void;
    provenance?: UgcProvenance;
    /**
     * Fired when a save actually persisted one of the DPMP-specific fields.
     * AIDEV-NOTE: These resources are not package-gated, so a user with the add-on OFF can
     * capture a healing record the product then will not show them. That is the moment to
     * offer the add-on.
     */
    onDpmpFieldsSaved?: () => void;
}) {
    const hasNameField = fields.some((f) => f.key === "name");
    const ageField = fields.find((f) => f.key === "age");
    const genderField = fields.find((f) => f.key === "gender");
    const classificationField = fields.find((f) => f.key === "type_of_part");
    const classificationOptions =
        classificationField?.options ?? CLASSIFICATION_FALLBACK_OPTIONS;
    // AIDEV-NOTE: The part's inferred type PRE-POPULATES the Type dropdown and SAVES with the
    // card if the user leaves it — they'll change it if it's wrong (Adam, Jul 7 2026).
    const inferredClassificationOption = resolveClassificationOption(
        partType,
        classificationOptions,
    );

    const [values, setValues] = useState<Record<string, string>>(() => {
        const seed: Record<string, string> = {};
        if (inferredClassificationOption) {
            seed.type_of_part = inferredClassificationOption;
        }
        return seed;
    });
    const [saveState, setSaveState] = useState<SaveState>("idle");
    // AIDEV-NOTE: EVO-2477 — the baseline seeds from the SAME inferred classification `values`
    // does, so a card the user has only looked at is not dirty. The pre-filled type is ours, not
    // theirs; only their own edits are work worth warning about.
    const [savedSnapshot, setSavedSnapshot] = useState(() =>
        captureSnapshot(
            inferredClassificationOption
                ? { type_of_part: inferredClassificationOption }
                : {},
        ),
    );
    const [nameBaseline, setNameBaseline] = useState(partName ?? "");
    // AIDEV-NOTE: "New Role" is a separate field from "Role" (Michelle Glass, Jul 7 2026 —
    // protectors capture previous role AND preferred new role). Synthesized here when the DB
    // experience config doesn't provide it, so no config migration is needed. Persists through
    // the same user-fields route; pcc-card-attributes folds it into behavioralExpression.
    const configNewRoleField = fields.find((f) => f.key === "new_role");
    const newRoleField: CaptureField = configNewRoleField ?? {
        key: "new_role",
        label: "New Role",
        prompt: "What new role is this part taking on, now that it doesn't have to do its old job?",
        placeholder: "This part's preferred new role is...",
    };
    // AIDEV-NOTE: General journal box (Michelle Glass, Jul 7 2026) — every resource capture gets a
    // free-form "anything else" box so users aren't limited to the structured fields. Saved as a
    // passthrough attribute; its prose feeds the same add-only LLM augmentation pass
    // (part-profile/processing-requested) that mines sessions/journals, so what they write can fill
    // inferred fields (e.g. "seems not to accept it isn't Self" → self-like part hint).
    const configJournalField = fields.find((f) => f.key === "general_journal");
    const generalJournalField: CaptureField = configJournalField ?? {
        key: "general_journal",
        label: "Journal",
        prompt: "Anything else you noticed that doesn't fit the boxes above? Write freely.",
        placeholder: "Anything else about this part...",
    };
    // Fields saved as notes (everything except the real-name editor). The synthesized new_role and
    // general_journal ride along so their labels + values always serialize with the card, even
    // while hidden.
    const noteFields = [
        ...fields.filter((f) => f.key !== "name"),
        ...(configNewRoleField ? [] : [newRoleField]),
        ...(configJournalField ? [] : [generalJournalField]),
    ];
    const compactRowKeys = ["age", "gender", "type_of_part"];
    const rawAccordionFields = noteFields.filter(
        (f) => !compactRowKeys.includes(f.key),
    );
    // AIDEV-NOTE: Conditional healing fields by classification (Michelle Glass, Jul 7 2026).
    // The classification value (pre-populated from the inferred type, user-editable) drives
    // which fields render; raw partType is the fallback for inferred types that don't match a
    // dropdown option (e.g. free-text pipeline types).
    //   - Protectors (manager/self-like manager/firefighter/protector): Role + New Role, with the
    //     full exile unburdening fields behind a small deferred link (minority use case — some
    //     protectors do go through their own witnessing/unburdening).
    //   - Exiles: the unburdening/retrieval/reclaimed-qualities fields, and NO Role field at
    //     all (Adam, Sep 2026 — roles are a protector concept; exiles heal via retrieval/
    //     unburdening, not into a role).
    // Hidden fields keep any saved value (rawSave still serializes all noteFields), so switching
    // type never loses data.
    const classificationValue =
        (values.type_of_part ?? "").trim() || (partType ?? "");
    const isProtectorClass = /manager|firefighter|protector/i.test(
        classificationValue,
    );
    const isExileClass = /exile/i.test(classificationValue);
    const [showProtectorUnburdening, setShowProtectorUnburdening] =
        useState(false);
    const unburdeningKeys = [
        "where_retrieved",
        "what_unburdened",
        "qualities_reclaimed",
    ];
    const roleField = rawAccordionFields.find((f) => f.key === "roles");
    const showNewRole = isProtectorClass;
    const otherAccordionFields = rawAccordionFields.filter((f) => {
        if (f.key === "roles" || f.key === "new_role") return false;
        // The journal box renders separately at the very bottom (below the deferred link).
        if (f.key === "general_journal") return false;
        if (unburdeningKeys.includes(f.key)) {
            // Exiles always see the unburdening fields; protectors only after the deferred
            // "Show full unburdening fields" link; unknown types keep the legacy behavior
            // (retrieval hidden, the rest visible).
            if (isExileClass) return true;
            if (isProtectorClass) return showProtectorUnburdening;
            return f.key !== "where_retrieved";
        }
        return true;
    });
    // Role never renders for exiles; a hidden field keeps any saved value (rawSave still
    // serializes all noteFields), so switching type never loses data.
    const accordionFields = [
        ...(roleField && !isExileClass ? [roleField] : []),
        ...(showNewRole ? [newRoleField] : []),
        ...otherAccordionFields,
    ];

    // AIDEV-NOTE: Single-open accordion — only one step is expanded at a time so the panel
    // stays short and fits the viewport without scrolling. Opens on "About this part" when
    // there is one (handoff 2d), otherwise on the first note field.
    const [openKey, setOpenKey] = useState<string | null>(
        hasNameField || ageField || genderField || classificationField
            ? ABOUT_STEP_KEY
            : (accordionFields[0]?.key ?? null),
    );

    // AIDEV-NOTE: Tracks whether the classification was chosen by the user rather than
    // pre-filled from inference, which is what the "Inferred" tag reflects.
    const [typeEdited, setTypeEdited] = useState(false);

    // AIDEV-NOTE: Local name edit is deferred like everything else — the inline editor only mutates
    // local state; the real /rename fires inside rawSave (folded in) when the name actually changed.
    const [localName, setLocalName] = useState(partName ?? "");
    const localNameRef = useRef(localName);
    localNameRef.current = localName;
    // AIDEV-NOTE: Baseline name to diff against so Save only renames when the user changed it. Kept
    // in a ref updated by the same effect that syncs localName from the (host-authoritative) prop.
    const savedNameRef = useRef(partName ?? "");
    useEffect(() => {
        setLocalName(partName ?? "");
        savedNameRef.current = partName ?? "";
        setNameBaseline(partName ?? "");
    }, [partName]);

    // AIDEV-NOTE: EVO-2477 — the rename rides on the same Save as the fields, so an edited name is
    // unsaved work too. `__name` cannot collide with a CaptureField key: those come from the
    // experience config and are plain attribute names.
    const currentSnapshot = captureSnapshot({
        ...values,
        __name:
            localName.trim() === nameBaseline.trim() ? "" : localName.trim(),
    });
    useUnsavedCaptureWarning(
        currentSnapshot !== EMPTY_CAPTURE_SNAPSHOT &&
            currentSnapshot !== savedSnapshot,
    );

    const valuesRef = useRef(values);
    valuesRef.current = values;
    // AIDEV-NOTE: Once the user edits, in-flight hydration must not clobber their input.
    const touchedRef = useRef(false);

    // AIDEV-NOTE: Hydrate from the part's durable part_profile so re-opening on the resource page
    // holds what was written. Saves upsert in place server-side (keyed on partId), so no ids are
    // needed here.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(`/api/parts/${partId}/user-fields`);
                if (!res.ok) return;
                const data = (await res.json()) as {
                    profile?: {
                        fields: Array<{ key: string; content: string }>;
                        labels: Record<string, string>;
                    } | null;
                };
                if (cancelled || touchedRef.current || !data.profile) return;
                const loaded: Record<string, string> = {};
                for (const f of data.profile.fields) loaded[f.key] = f.content;
                // Keep the inferred-type pre-population when the saved card has no
                // classification of its own.
                if (!loaded.type_of_part && inferredClassificationOption) {
                    loaded.type_of_part = inferredClassificationOption;
                }
                setValues(loaded);
            } catch {
                // Soft-fail: panel just starts empty if hydration fails.
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partId]);

    // AIDEV-NOTE: labels/payload span all note fields (accordion + age + gender) so the
    // compact age/gender row persists alongside the accordion answers in the same note.
    const labels = Object.fromEntries(noteFields.map((f) => [f.key, f.label]));

    // AIDEV-NOTE: The single Save handler. Folds the rename in FIRST (name uses a separate /rename
    // endpoint) when the user changed it, then upserts the card fields. Returns "empty" only when
    // there's neither a name change nor any field content. The route upserts the part's profile head
    // + today's revision from partId alone — no ids in the request or response.
    const rawSave = useCallback(async (): Promise<SaveResult> => {
        const fieldPayload = noteFields
            .map((f) => ({
                key: f.key,
                content: valuesRef.current[f.key] ?? "",
            }))
            .filter((f) => f.content.trim());

        const newName = localNameRef.current.trim();
        const nameChanged = !!newName && newName !== savedNameRef.current;

        if (fieldPayload.length === 0 && !nameChanged) return "empty";

        setSaveState("saving");
        try {
            if (nameChanged) {
                const renameRes = await fetch(`/api/parts/${partId}/rename`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name: newName, ...provenance }),
                });
                if (!renameRes.ok)
                    throw new Error(`Rename failed: ${renameRes.status}`);
                savedNameRef.current = newName;
                onRename?.(partId, newName);
            }
            if (fieldPayload.length > 0) {
                const res = await fetch(`/api/parts/${partId}/user-fields`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        title,
                        fields: fieldPayload,
                        labels,
                        ...provenance,
                    }),
                });
                if (!res.ok) throw new Error(`Save failed: ${res.status}`);
                if (
                    fieldPayload.some((field) =>
                        DPMP_CAPTURE_FIELD_KEYS.has(field.key),
                    )
                ) {
                    onDpmpFieldsSaved?.();
                }
            }
            setNameBaseline(savedNameRef.current);
            setSavedSnapshot(captureSnapshot({ ...valuesRef.current }));
            setSaveState("saved");
            return "saved";
        } catch {
            // AIDEV-NOTE: Soft-fail — keep the user's text on screen; they can retry with Save.
            setSaveState("error");
            return "error";
        }
    }, [
        noteFields,
        labels,
        partId,
        title,
        onRename,
        provenance,
        onDpmpFieldsSaved,
    ]);
    const save = useSerializedSave(rawSave);

    // AIDEV-NOTE: Edits mutate local state only — nothing persists until Save is clicked.
    const handleChange = (key: string, value: string) => {
        touchedRef.current = true;
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    // AIDEV-NOTE: Magic-wand — ask the server to draft one field from the part's attributes-to-date.
    // Returns the suggestion string (or "" if none); the AccordionField populates its textarea via
    // onChange, so it flows through handleChange → touchedRef like any typed edit (still Save-gated).
    const suggestField = useCallback(
        async (key: string, label: string): Promise<string> => {
            const res = await fetch(`/api/parts/${partId}/suggest-field`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fieldKey: key, label }),
            });
            if (!res.ok) throw new Error(`Suggest failed: ${res.status}`);
            const data = (await res.json()) as { suggestion?: string };
            return data.suggestion?.trim() ?? "";
        },
        [partId],
    );

    // AIDEV-NOTE: Age (inline editor) / gender + classification (dropdowns) mutate local state only,
    // like the accordion fields — they persist with everything else when Save is clicked.
    const commitValue = (key: string, value: string) => {
        touchedRef.current = true;
        if (key === "type_of_part") setTypeEdited(true);
        const next = { ...valuesRef.current, [key]: value };
        valuesRef.current = next;
        setValues(next);
    };

    // AIDEV-NOTE: The type arrives pre-filled from the pipeline's inference, which the
    // "Inferred" tag discloses so the user knows to correct it rather than wondering where
    // it came from. It stops being inference the moment they pick one themselves.
    const typeIsInferred =
        !typeEdited &&
        Boolean(inferredClassificationOption) &&
        values.type_of_part === inferredClassificationOption;

    // AIDEV-NOTE: "Done" means the USER supplied something, so a pre-filled name and an
    // inferred type do not tick the step off on their own. A type that differs from the
    // inference DOES count, including one hydrated from a previous save — `typeEdited` only
    // covers this session, so without that check reopening a saved card showed step 1 as
    // untouched while every other step read its hydrated value.
    const aboutStepTouched =
        Boolean(values.age?.trim()) ||
        Boolean(values.gender?.trim()) ||
        typeEdited ||
        (Boolean(values.type_of_part?.trim()) &&
            values.type_of_part !== inferredClassificationOption) ||
        localName.trim() !== savedNameRef.current.trim();

    // AIDEV-NOTE: Step order (handoff 2d) — About this part, then the type-conditional
    // fields, then Journal last, which is the order the fields were already in. The ids
    // double as the single-open accordion's keys, with ABOUT_STEP_KEY standing in for the
    // step that is a group of attributes rather than one note field.
    const hasAboutStep = Boolean(
        hasNameField || ageField || genderField || classificationField,
    );
    const stepKeys = [
        ...(hasAboutStep ? [ABOUT_STEP_KEY] : []),
        ...accordionFields.map((f) => f.key),
        generalJournalField.key,
    ];

    const advanceFrom = (key: string) => {
        const next = stepKeys[stepKeys.indexOf(key) + 1];
        setOpenKey(next ?? null);
    };
    const toggle = (key: string) => setOpenKey(openKey === key ? null : key);
    const stepNumber = (key: string) => stepKeys.indexOf(key) + 1;
    const isLastStep = (key: string) =>
        stepKeys.indexOf(key) === stepKeys.length - 1;

    return (
        <div className="flex flex-col gap-4">
            {/* AIDEV-NOTE: "from", not "about" — the question is what the PART told you,
                which is the whole posture of the practice (handoff 2d/2e). */}
            <h3 className="font-display text-[22px] font-semibold leading-[1.2] text-brand-forest-800">
                What did you learn from this part?
            </h3>
            <CaptureDestinationNote />

            <div className="flex flex-col gap-1">
                {hasAboutStep && (
                    <AboutPartStep
                        index={stepNumber(ABOUT_STEP_KEY)}
                        isOpen={openKey === ABOUT_STEP_KEY}
                        isDone={aboutStepTouched}
                        isLast={isLastStep(ABOUT_STEP_KEY)}
                        onToggle={() => toggle(ABOUT_STEP_KEY)}
                        onNext={() => advanceFrom(ABOUT_STEP_KEY)}
                        onSkip={() => advanceFrom(ABOUT_STEP_KEY)}
                        hasNameField={hasNameField}
                        localName={localName}
                        setLocalName={setLocalName}
                        ageField={ageField}
                        genderField={genderField}
                        classificationField={classificationField}
                        classificationOptions={classificationOptions}
                        typeIsInferred={typeIsInferred}
                        values={values}
                        commitValue={commitValue}
                    />
                )}

                {accordionFields.map((field) => (
                    <AccordionField
                        key={field.key}
                        field={field}
                        index={stepNumber(field.key)}
                        isOpen={openKey === field.key}
                        isDone={Boolean(values[field.key]?.trim())}
                        isLast={isLastStep(field.key)}
                        onToggle={() => toggle(field.key)}
                        onNext={() => advanceFrom(field.key)}
                        onSkip={() => advanceFrom(field.key)}
                        value={values[field.key] ?? ""}
                        onChange={(v) => handleChange(field.key, v)}
                        onFocus={onFieldFocus}
                        onSuggest={() => suggestField(field.key, field.label)}
                    />
                ))}

                {/* AIDEV-NOTE: Free-form journal box — always last, for every part type. */}
                <AccordionField
                    field={generalJournalField}
                    index={stepNumber(generalJournalField.key)}
                    isOpen={openKey === generalJournalField.key}
                    isDone={Boolean(values[generalJournalField.key]?.trim())}
                    isLast={isLastStep(generalJournalField.key)}
                    onToggle={() => toggle(generalJournalField.key)}
                    onNext={() => advanceFrom(generalJournalField.key)}
                    onSkip={() => advanceFrom(generalJournalField.key)}
                    value={values[generalJournalField.key] ?? ""}
                    onChange={(v) => handleChange(generalJournalField.key, v)}
                    onFocus={onFieldFocus}
                />
            </div>

            {/* AIDEV-NOTE: Deferred unburdening reveal for protectors (Michelle Glass, Jul 7
                2026) — most protectors just capture Role/New Role, but some go through their own
                witnessing/unburdening. A small link keeps the card clean for the majority while
                keeping those details capturable. */}
            {isProtectorClass && !showProtectorUnburdening && (
                <button
                    type="button"
                    onClick={() => setShowProtectorUnburdening(true)}
                    className="self-start text-sm text-brand-terra-600 underline-offset-2 hover:underline"
                >
                    Show full unburdening fields
                </button>
            )}

            {/* AIDEV-NOTE: The one and only persistence action — Save (inline resource page, pops a
                toast) or Save & Close (player, persists then dismisses via onClose). */}
            <SaveButtonRow
                state={saveState}
                onSave={save}
                onClose={onClose}
                savedToPart={{
                    partId,
                    partName: localName || partName,
                    captureKind: "pcc_card",
                }}
            />
        </div>
    );
}

// AIDEV-NOTE: note kind (Parts Biography) — narrative prose across a few prompts composed into a
// single note body and persisted via /api/parts/:id/notes (POST creates, then PATCH updates the
// same note by returned noteId, so re-saves don't duplicate). Persists ONLY on the explicit Save.
function NoteCapture({
    partId,
    title,
    partName,
    fields,
    onFieldFocus,
    onClose,
    provenance,
}: {
    partId: string;
    title: string;
    // AIDEV-NOTE: EVO-2475 — display only; it names the part in the saved-to-part toast.
    partName?: string;
    fields: CaptureField[];
    onFieldFocus?: () => void;
    onClose?: () => void;
    provenance?: UgcProvenance;
}) {
    const [values, setValues] = useState<Record<string, string>>({});
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const [openKey, setOpenKey] = useState<string | null>(
        fields[0]?.key ?? null,
    );
    // AIDEV-NOTE: EVO-2477 — this capture HYDRATES from the existing biography note, so the
    // baseline has to move when hydration lands as well as when Save succeeds; otherwise simply
    // opening a part that already has a biography would read as unsaved work.
    const [savedSnapshot, setSavedSnapshot] = useState(EMPTY_CAPTURE_SNAPSHOT);
    const currentSnapshot = captureSnapshot(values);
    useUnsavedCaptureWarning(
        currentSnapshot !== EMPTY_CAPTURE_SNAPSHOT &&
            currentSnapshot !== savedSnapshot,
    );

    const valuesRef = useRef(values);
    valuesRef.current = values;
    // AIDEV-NOTE: noteId persists so blur/close re-saves PATCH the same note in place.
    const noteIdRef = useRef<string | null>(null);
    const touchedRef = useRef(false);

    // AIDEV-NOTE: Hydrate from the part's existing biography note (type="biography", one per part)
    // so re-opening on the resource page holds the prior text and PATCHes the same note in place.
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            try {
                const res = await fetch(
                    `/api/parts/${partId}/notes?type=biography`,
                );
                if (!res.ok) return;
                const data = (await res.json()) as {
                    note?: { id: string; body: string } | null;
                };
                if (cancelled || touchedRef.current || !data.note) return;
                noteIdRef.current = data.note.id;
                const hydrated = parseComposedBody(data.note.body, fields);
                setValues(hydrated);
                setSavedSnapshot(captureSnapshot(hydrated));
            } catch {
                // Soft-fail: panel just starts empty if hydration fails.
            }
        })();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partId]);

    // AIDEV-NOTE: Compose the answered prompts into one prose body, labelled per section, so the
    // note reads as a coherent biography and the LLM augmentation extracts inferred attributes.
    const composeBody = useCallback(() => {
        return fields
            .map((f) => ({
                label: f.label,
                content: valuesRef.current[f.key] ?? "",
            }))
            .filter((s) => s.content.trim())
            .map((s) => `${s.label}\n${s.content.trim()}`)
            .join("\n\n");
    }, [fields]);

    const rawSave = useCallback(async (): Promise<SaveResult> => {
        const content = composeBody();
        if (!content.trim()) return "empty";

        setSaveState("saving");
        try {
            if (noteIdRef.current) {
                const res = await fetch(`/api/parts/${partId}/notes`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    // AIDEV-NOTE: title rides along so pre-existing "Journal Entry"-titled
                    // biography notes get retitled to the capture context in place.
                    body: JSON.stringify({
                        noteId: noteIdRef.current,
                        content,
                        title,
                    }),
                });
                if (!res.ok) throw new Error(`Save failed: ${res.status}`);
            } else {
                const res = await fetch(`/api/parts/${partId}/notes`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // AIDEV-NOTE: noteType tags the interaction so this note is re-openable (loader).
                    // title = capture context (resource title) → timeline heading.
                    body: JSON.stringify({
                        content,
                        noteType: "biography",
                        title,
                        // AIDEV-NOTE: POST only — provenance is first-write-wins, so the PATCH
                        // branch above deliberately omits it.
                        ...provenance,
                    }),
                });
                if (!res.ok) throw new Error(`Save failed: ${res.status}`);
                const data = (await res.json()) as {
                    note?: { id?: string };
                };
                if (data.note?.id) noteIdRef.current = data.note.id;
            }
            setSavedSnapshot(captureSnapshot(valuesRef.current));
            setSaveState("saved");
            return "saved";
        } catch {
            setSaveState("error");
            return "error";
        }
    }, [composeBody, partId, title, provenance]);
    const save = useSerializedSave(rawSave);

    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-xl font-semibold leading-snug text-brand-forest-800">
                What did you learn from this part?
            </h3>
            <CaptureDestinationNote />

            <div className="flex flex-col gap-3">
                {fields.map((field) => (
                    <AccordionField
                        key={field.key}
                        field={field}
                        isOpen={openKey === field.key}
                        onToggle={() =>
                            setOpenKey(openKey === field.key ? null : field.key)
                        }
                        value={values[field.key] ?? ""}
                        onChange={(v) => {
                            touchedRef.current = true;
                            setValues((prev) => ({ ...prev, [field.key]: v }));
                        }}
                        onFocus={onFieldFocus}
                    />
                ))}
            </div>

            <SaveButtonRow
                state={saveState}
                onSave={save}
                onClose={onClose}
                savedToPart={{ partId, partName, captureKind: "note" }}
            />
        </div>
    );
}

// AIDEV-NOTE: part_note kind — a STANDARD part journal note captured in-meditation (NOT the DPMP
// structured biography/PCC). A single free-text textarea → /api/parts/:id/notes with the default
// noteType ("journal"). POST creates on the first Save, then subsequent Saves PATCH the same note by
// returned noteId so a single meditation yields one growing note. Append-style: a fresh mount
// (re-open after close) starts a NEW note — the daily practice makes a new entry each time — so
// there's no cross-remount hydration. `prompt`/`placeholder` come from the experience's first
// captureField.
function PartNoteCapture({
    partId,
    title,
    partName,
    field,
    onFieldFocus,
    onClose,
    provenance,
}: {
    partId: string;
    title: string;
    // AIDEV-NOTE: EVO-2475 — display only; it names the part in the saved-to-part toast.
    partName?: string;
    field?: CaptureField;
    onFieldFocus?: () => void;
    onClose?: () => void;
    provenance?: UgcProvenance;
}) {
    const [text, setText] = useState("");
    const [open, setOpen] = useState(true);
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const textRef = useRef(text);
    textRef.current = text;
    // AIDEV-NOTE: EVO-2477 — compared against what was last SAVED, not against empty, so text
    // added after a save counts as unsaved again. A cleared box is never dirty: the saved note
    // still exists server-side, so there is nothing to lose by leaving.
    const [savedText, setSavedText] = useState("");
    useUnsavedCaptureWarning(
        text.trim().length > 0 && text.trim() !== savedText,
    );
    // AIDEV-NOTE: Render inside the same AccordionField card as the biography/PCC capture so the
    // DPMP part-of-the-day note matches those surfaces visually. Single field, open by default.
    const noteField: CaptureField = field ?? { key: "note", label: "Notes" };
    // AIDEV-NOTE: noteId persists so blur/close re-saves PATCH the same note in place this session.
    const noteIdRef = useRef<string | null>(null);

    const rawSave = useCallback(async (): Promise<SaveResult> => {
        const content = textRef.current.trim();
        if (!content) return "empty";

        setSaveState("saving");
        try {
            if (noteIdRef.current) {
                const res = await fetch(`/api/parts/${partId}/notes`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        noteId: noteIdRef.current,
                        content,
                    }),
                });
                if (!res.ok) throw new Error(`Save failed: ${res.status}`);
            } else {
                const res = await fetch(`/api/parts/${partId}/notes`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    // AIDEV-NOTE: noteType omitted → the route defaults to a standard "journal"
                    // note. title = capture context (resource title) → timeline heading.
                    // AIDEV-NOTE: provenance on POST only (first-write-wins, like the PATCH above).
                    body: JSON.stringify({ content, title, ...provenance }),
                });
                if (!res.ok) throw new Error(`Save failed: ${res.status}`);
                const data = (await res.json()) as { note?: { id?: string } };
                if (data.note?.id) noteIdRef.current = data.note.id;
            }
            setSavedText(content);
            setSaveState("saved");
            return "saved";
        } catch {
            setSaveState("error");
            return "error";
        }
    }, [partId, title, provenance]);
    const save = useSerializedSave(rawSave);

    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-xl font-semibold leading-snug text-brand-forest-800">
                What did you learn from this part?
            </h3>
            <CaptureDestinationNote />
            <AccordionField
                field={noteField}
                isOpen={open}
                onToggle={() => setOpen((o) => !o)}
                value={text}
                onChange={(v) => setText(v)}
                onFocus={onFieldFocus}
            />
            <SaveButtonRow
                state={saveState}
                onSave={save}
                onClose={onClose}
                savedToPart={{ partId, partName, captureKind: "part_note" }}
            />
        </div>
    );
}

// AIDEV-NOTE: name kind (Discovering a Part's True Name) — a single inline editor that renames the
// real part via /api/parts/:id/rename. No card/note is persisted. The inline editor mutates local
// state only; the actual rename fires on the explicit Save (folded into rawSave when changed).
function NameCapture({
    partId,
    title: _title,
    partName,
    onRename,
    onClose,
    provenance,
}: {
    partId: string;
    title: string;
    partName?: string;
    onRename?: (partId: string, newName: string) => void;
    onClose?: () => void;
    provenance?: UgcProvenance;
}) {
    const [localName, setLocalName] = useState(partName ?? "");
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const localNameRef = useRef(localName);
    localNameRef.current = localName;
    const savedNameRef = useRef(partName ?? "");
    // AIDEV-NOTE: EVO-2477 — savedNameRef alone cannot drive the warning: a ref mutation does not
    // re-render, so the hook would keep whichever value it was last given. Mirrored into state and
    // updated at both sites that move the ref.
    const [savedName, setSavedName] = useState(partName ?? "");
    useUnsavedCaptureWarning(
        localName.trim().length > 0 && localName.trim() !== savedName.trim(),
        "This part's new name hasn't been saved yet. Leave without saving it?",
    );
    useEffect(() => {
        setLocalName(partName ?? "");
        savedNameRef.current = partName ?? "";
        setSavedName(partName ?? "");
    }, [partName]);

    const rawSave = useCallback(async (): Promise<SaveResult> => {
        const newName = localNameRef.current.trim();
        if (!newName || newName === savedNameRef.current) return "empty";

        setSaveState("saving");
        try {
            const res = await fetch(`/api/parts/${partId}/rename`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name: newName, ...provenance }),
            });
            if (!res.ok) throw new Error(`Rename failed: ${res.status}`);
            savedNameRef.current = newName;
            setSavedName(newName);
            setSaveState("saved");
            onRename?.(partId, newName);
            return "saved";
        } catch {
            setSaveState("error");
            return "error";
        }
    }, [partId, onRename, provenance]);
    const save = useSerializedSave(rawSave);

    return (
        <div className="flex flex-col gap-5">
            <h3 className="text-xl font-semibold leading-snug text-brand-forest-800">
                What did you learn from this part?
            </h3>
            <div className="rounded-xl border border-brand-terra-100 bg-white px-5 py-4">
                <p className="text-xs font-medium uppercase tracking-wide text-brand-terra-500">
                    Name
                </p>
                {/* AIDEV-NOTE: commitOnBlur (EVO-2215) — same silent-loss trap the PCC card's
                    name/age editors were cured of, but this standalone name panel was missed:
                    typing a name and clicking Save & Close (never Enter/check) discarded the
                    value, rawSave saw "empty", and the overlay closed as if it saved. This is
                    the Part Naming Meditation's ONLY capture, so the whole exercise saved
                    nothing (prod case: rename journaled as lost, 2026-09-13). */}
                <InlineEditableText
                    value={localName}
                    onSave={async (v) => setLocalName(v)}
                    commitOnBlur
                    alwaysShowPencil
                    className="mt-1 text-lg font-semibold text-brand-forest-800"
                    inputClassName="h-9 text-lg"
                    pencilClassName="text-brand-terra-500"
                    placeholder="Name this part"
                />
            </div>
            <SaveButtonRow
                state={saveState}
                onSave={save}
                onClose={onClose}
                savedToPart={{
                    partId,
                    // AIDEV-NOTE: The NEW name, not the one the panel opened with — this kind's
                    // whole output is the rename, so the confirmation has to show what it now
                    // says. `savedName` lags by a render (it is set in the same tick the toast
                    // fires), so read the editor's value.
                    partName: localName || partName,
                    captureKind: "name",
                }}
            />
        </div>
    );
}

// AIDEV-NOTE: meditation_journal kind — the universal default for general/non-DPMP meditations.
// Reflection opens expanded after playback and persists to ONE note via /api/meditation/journal.
// Every save sends the current draft; persistence requires Save & Close, and skipping is allowed.
const REFLECT_FIELD: CaptureField = {
    key: "reflection",
    label: "Reflect",
    prompt: "Having finished this meditation, what do you notice now? Is there anything you'd like to take with you?",
    placeholder: "I noticed...",
};

function ReflectCapture({
    sessionId,
    partInteractionId,
    meditationType,
    meditationId,
    onFieldFocus,
    onClose,
    onSkip,
    provenance,
}: {
    sessionId?: string;
    partInteractionId?: string;
    meditationType?: string;
    /** Concrete meditation row to save against; falls back to the context lookup. */
    meditationId?: string | null;
    onFieldFocus?: () => void;
    onClose?: () => void;
    onSkip?: () => void;
    // AIDEV-NOTE: Resource exercises have no sessionId/partInteractionId — experienceId is the
    // only anchor their meditation row has, so without it Save silently no-op'd in an exercise.
    provenance?: UgcProvenance;
}) {
    const experienceId = provenance?.experienceId;
    const [reflection, setReflection] = useState("");
    // AIDEV-NOTE: Completion capture starts expanded so the reflection prompt is immediately visible.
    const [isReflectionOpen, setIsReflectionOpen] = useState(true);
    const [saveState, setSaveState] = useState<SaveState>("idle");
    const reflectionRef = useRef(reflection);
    reflectionRef.current = reflection;
    // AIDEV-NOTE: EVO-2477 — see PartNoteCapture; same baseline-is-what-was-saved rule.
    const [savedReflection, setSavedReflection] = useState("");
    useUnsavedCaptureWarning(
        reflection.trim().length > 0 && reflection.trim() !== savedReflection,
    );

    const rawSave = useCallback(async (): Promise<SaveResult> => {
        const r = reflectionRef.current.trim();
        if (!r) return "empty";
        if (!sessionId && !partInteractionId && !experienceId) return "empty";

        setSaveState("saving");
        try {
            const res = await fetch("/api/meditation/journal", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    sessionId,
                    partInteractionId,
                    experienceId,
                    meditationType,
                    meditationId: meditationId ?? undefined,
                    reflection: r,
                    ...provenance,
                }),
            });
            if (!res.ok) throw new Error(`Save failed: ${res.status}`);
            setSavedReflection(r);
            setSaveState("saved");
            return "saved";
        } catch {
            setSaveState("error");
            return "error";
        }
    }, [
        sessionId,
        partInteractionId,
        experienceId,
        meditationType,
        meditationId,
        provenance,
    ]);
    const save = useSerializedSave(rawSave);

    return (
        <div className="flex flex-col gap-3">
            <AccordionField
                field={REFLECT_FIELD}
                isOpen={isReflectionOpen}
                onToggle={() => setIsReflectionOpen((open) => !open)}
                value={reflection}
                onChange={setReflection}
                onFocus={onFieldFocus}
            />
            <SaveButtonRow
                state={saveState}
                onSave={save}
                onClose={onClose}
                onSkip={onSkip}
            />
        </div>
    );
}

// AIDEV-NOTE: EVO-2475 — Gina asked for the exercise DESCRIPTION to say where a practice's
// notes end up. Adam ruled against that (it is setup copy answering a question nobody has yet)
// in favour of saying it at the moment it becomes true, with a way to go and look. Hence this:
// fired on a real save that anchored to a part, naming the part and offering its page.
//
// Both save paths needed it and for different reasons. In the fullscreen player (onClose set)
// a successful save said NOTHING AT ALL — the overlay just closed, which is the hole Gina fell
// into. On the inline resource panel it said a bare "Saved", which confirms the write but not
// where it went.
const SAVED_TO_PART_TOAST_MS = 15_000;

// AIDEV-NOTE: What the toast says each kind put on the part's page. Reviewed by writer-agent
// against what the page ACTUALLY renders, which is why the wording is not "your note":
//   - part_note / note both POST to /api/parts/:id/notes and surface as a Timeline row labelled
//     "Journal entry" (part-compact-timeline.tsx), so "note" would send someone hunting for a
//     word the page never shows.
//   - pcc_card writes a Timeline row AND the durable fields in the part's Details section, and
//     the Details half is the one that lasts — so it names both.
//   - name is a rename. It writes nothing to the Timeline, and the title already carries the new
//     name, so it gets no second line rather than a sentence that would be false. Its TITLE also
//     differs — see toastSavedToPart.
const SAVED_TO_PART_DESCRIPTION: Partial<Record<CaptureKind, string>> = {
    part_note: "Your journal entry is on the part's page, under Timeline.",
    note: "Your journal entry is on the part's page, under Timeline.",
    pcc_card:
        "Your card is on the part's page, under Timeline, and what you filled in now shows under Details.",
};

/**
 * Confirm a capture that landed on a part, and offer that part's page.
 *
 * AIDEV-NOTE: 15s is a product decision (Adam), not a default — sonner's own default is 4s,
 * which is too short to notice an action button, read a part name and decide to follow it,
 * especially right after closing a meditation. `duration` is per-toast, so this does not
 * affect any other toast in the app.
 * AIDEV-NOTE: The action is withheld when the viewer is ALREADY on that part's page (the part
 * detail route mounts this same panel for its own check-ins), where the button would be a link
 * to where they are standing. The confirmation still fires; only the link drops.
 * AIDEV-NOTE: The `name` kind says "Renamed to", not "Saved to" (Adam, EVO-2475). Every other
 * kind files something INTO the part's record, so "Saved to <part>" is literally what happened;
 * a rename changes the part itself, and "Saved to <new name>" reads as though something was
 * filed under the new name. Only the title differs — duration, description rule and the
 * click-through are identical across kinds.
 */
function toastSavedToPart({
    partId,
    partName,
    captureKind,
    onSameRoute,
    navigate,
}: {
    partId: string;
    partName?: string;
    captureKind: CaptureKind;
    onSameRoute: boolean;
    navigate: (to: string) => void;
}) {
    // A rename with no name cannot happen — the name IS what was saved, and an empty editor
    // returns "empty" rather than "saved" — so the generic fallback stays "Saved to this part".
    const message = !partName
        ? "Saved to this part"
        : captureKind === "name"
          ? `Renamed to ${partName}`
          : `Saved to ${partName}`;
    if (onSameRoute) {
        toast.success(message);
        return;
    }
    toast.success(message, {
        description: SAVED_TO_PART_DESCRIPTION[captureKind],
        duration: SAVED_TO_PART_TOAST_MS,
        action: {
            label: "Open the part's page",
            onClick: () => navigate(`/parts/${partId}`),
        },
    });
}

// AIDEV-NOTE: The sole persistence action for every capture kind (no autosave anymore). Runs the
// serialized save() and reacts to the outcome:
//   - A save that anchored to a part (savedToPart set, every kind but meditation_journal) pops
//     the saved-to-part toast above, on BOTH paths, and it replaces the bare "Saved".
//   - Inline resource page (no onClose): toasts on saved / error / empty, panel stays.
//   - Fullscreen player (onClose set): label is "Save & Close"; on a non-error outcome (saved OR
//     empty — nothing to save is still a valid close) it invokes onClose to dismiss the overlay; on
//     error it keeps the panel open and toasts so the user can retry.
function SaveButtonRow({
    state,
    onSave,
    onClose,
    onSaved,
    onSkip,
    savedToPart,
}: {
    state: SaveState;
    onSave: () => Promise<SaveResult>;
    onClose?: () => void;
    /** Fired only on a real save, so a caller can react to what was captured. */
    onSaved?: () => void;
    /** Close without saving; renders a "Skip" button when provided. */
    onSkip?: () => void;
    // AIDEV-NOTE: EVO-2475 — present for every capture kind that anchors to a part, absent for
    // meditation_journal, which has no part record to send anyone to.
    savedToPart?: {
        partId: string;
        partName?: string;
        captureKind: CaptureKind;
    };
}) {
    const [clicking, setClicking] = useState(false);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const handleClick = async () => {
        setClicking(true);
        try {
            const result = await onSave();
            if (result === "saved") onSaved?.();
            if (result === "saved" && savedToPart) {
                toastSavedToPart({
                    ...savedToPart,
                    onSameRoute: pathname.startsWith(
                        `/parts/${savedToPart.partId}`,
                    ),
                    navigate,
                });
            }
            if (onClose) {
                if (result === "error")
                    toast.error("Couldn't save — please try again");
                else onClose();
            } else {
                if (result === "saved" && !savedToPart) toast.success("Saved");
                else if (result === "error")
                    toast.error("Couldn't save — please try again");
                else if (result !== "saved") toast("Nothing to save yet");
            }
        } finally {
            setClicking(false);
        }
    };
    return (
        <div className="flex items-center justify-end gap-3">
            <SaveIndicator state={state} />
            {onSkip ? (
                <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={onSkip}
                    disabled={clicking || state === "saving"}
                >
                    Skip
                </Button>
            ) : null}
            <Button
                type="button"
                size="sm"
                onClick={handleClick}
                disabled={clicking || state === "saving"}
                className="gap-1.5"
            >
                {clicking ? (
                    <Loader2 className="size-3.5 animate-spin" />
                ) : (
                    <Check className="size-3.5" />
                )}
                {onClose ? "Save & Close" : "Save"}
            </Button>
        </div>
    );
}

function SaveIndicator({ state }: { state: SaveState }) {
    if (state === "saving") {
        return (
            <span className="flex items-center gap-1 text-xs text-stone-400">
                <Loader2 className="size-3 animate-spin" />
                Saving
            </span>
        );
    }
    if (state === "saved") {
        return (
            <span className="flex items-center gap-1 text-xs text-stone-400">
                <Check className="size-3" />
                Saved
            </span>
        );
    }
    if (state === "error") {
        return (
            <span className="text-xs text-amber-600">Not saved — retrying</span>
        );
    }
    return null;
}
